"use client";

import { Suspense, useEffect, useState } from "react";
import { generatePKCE, generateState, getAuthorizeUrl, savePKCE } from "@/lib/auth";
import { ensureSessionOrRedirect } from "@/lib/api";
import { useTranslation } from "@/lib/i18n/context";
import { useSearchParams } from "@booking/core-ui/router";
import AuthCard from "@booking/core-ui/admin/components/AuthCard";
import SignInRefusal, { isRefusal } from "@booking/core-ui/admin/components/SignInRefusal";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import { runtimeConfig } from "@booking/core-ui/runtime-config";

function LoginForm() {
  const searchParams = useSearchParams();
  const [error, setError] = useState("");
  const [authenticating, setAuthenticating] = useState(false);
  const { t, langHref } = useTranslation();

  // A refusal is not a failed sign-in, and it does not get the same button.
  // The account was identified and turned away, so retrying the same flow
  // returns the same answer; SignInRefusal offers the one action that changes
  // it. Anything else in ?error= is not a refusal and falls through.
  const errorParam = searchParams.get("error");
  const refused = isRefusal(errorParam) ? errorParam : null;

  useEffect(() => {
    ensureSessionOrRedirect((path) => {
      if (path === "/login") return;
      window.location.href = langHref(path);
    }).then((payload) => {
      if (payload) {
        window.location.href = langHref("/dashboard");
      }
    });
  }, []);

  const handleLogin = async () => {
    setError("");
    setAuthenticating(true);
    try {
      const { codeVerifier, codeChallenge } = await generatePKCE();
      const state = generateState();
      const redirectUri = `${window.location.origin}/auth/callback`;
      savePKCE({ codeVerifier, state });
      const authorizeUrl = getAuthorizeUrl(codeChallenge, state, redirectUri);
      window.location.href = authorizeUrl;
    } catch {
      setAuthenticating(false);
      setError(t("loginInitFailed"));
    }
  };

  return (
    // The company, when the server knows it: five sign-in pages otherwise read an identical "管理".
    <AuthCard title={runtimeConfig.companyName || t("loginTitle")} subtitle={t("loginSubtitle")} logoUrl={runtimeConfig.logoUrl}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {refused ? (
        <SignInRefusal code={refused} />
      ) : (
        <Button
          variant="contained"
          fullWidth
          type="button"
          onClick={handleLogin}
          disabled={authenticating}
        >
          {authenticating ? t("authenticating") : t("signIn")}
        </Button>
      )}
    </AuthCard>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  );
}
